import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client.ts";
import type {
  Brand,
  DnsStatus,
  Domain,
  PostalInstance,
  PostalServer,
  Sender,
  SenderDefaults,
  Stream,
} from "../api/types.ts";
import { confirmAction, toast } from "../components/feedback.tsx";
import { Drawer, TableSkeleton } from "../components/ui.tsx";

/**
 * Domains &amp; senders.
 *
 * The screen that makes the sending configuration visible: which domains exist,
 * which Postal mail server each is bound to, whether its DNS and credentials are
 * actually working, and which addresses send from it.
 */

const DNS_PILL: Record<DnsStatus, string> = {
  ok: "ok",
  pending: "warn",
  invalid: "bad",
  missing: "bad",
};

function DnsBadge({ label, status }: { label: string; status: DnsStatus }) {
  return (
    <span className={`pill ${DNS_PILL[status]}`} title={`${label}: ${status}`}>
      {label}
    </span>
  );
}

export function DomainsPage() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });
  const instances = useQuery({
    queryKey: ["postal-instances"],
    queryFn: () => api.get<PostalInstance[]>("/config/postal-instances"),
  });
  const servers = useQuery({
    queryKey: ["postal-servers"],
    queryFn: () => api.get<PostalServer[]>("/config/postal-servers"),
  });
  const domains = useQuery({
    queryKey: ["domains"],
    queryFn: () => api.get<Domain[]>("/config/domains"),
  });
  const senders = useQuery({
    queryKey: ["senders"],
    queryFn: () => api.get<Sender[]>("/config/senders"),
  });

  const [actionError, setActionError] = useState<string | null>(null);

  const onMutated = (keys: string[]) => ({
    onSuccess: () => {
      setActionError(null);
      for (const key of keys) void qc.invalidateQueries({ queryKey: [key] });
    },
    onError: (err: Error) => setActionError(err.message),
  });

  const removeDomain = useMutation({
    mutationFn: (id: string) => api.del(`/config/domains/${id}`),
    // Its mail server and unused senders are removed with it.
    ...onMutated(["domains", "postal-servers", "senders"]),
  });

  const removeSender = useMutation({
    mutationFn: (id: string) =>
      api.del<{ deactivated: boolean; reason?: string }>(`/config/senders/${id}`),
    ...onMutated(["senders"]),
  });

  const patchDomain = useMutation({
    mutationFn: (args: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/config/domains/${args.id}`, args.body),
    ...onMutated(["domains"]),
  });

  if (domains.isLoading || brands.isLoading) {
    return <div className="page">Loading…</div>;
  }

  const brandFor = (id: string) => brands.data?.find((b) => b.id === id);
  const serverFor = (id: string) => servers.data?.find((s) => s.id === id);
  const instanceFor = (id: string | undefined) => instances.data?.find((i) => i.id === id);
  const sendersFor = (domainId: string) =>
    senders.data?.filter((s) => s.domainId === domainId) ?? [];

  return (
    <div className="page">
      <div className="page-header">
        <h1>Domains &amp; senders</h1>
        <p>
          Each sending domain has its own Postal mail server, so a problem on one cannot reach the
          others.
        </p>
      </div>

      {actionError && <div className="error">{actionError}</div>}

      {instances.data?.length === 0 && (
        <div className="card">
          <div className="card-body">
            <strong>No Postal installation registered yet.</strong>
            <p style={{ color: "var(--gray-500)", marginTop: 4 }}>
              A domain sends through a Postal mail server, so register your installation first under{" "}
              <Link to="/settings/postal">Postal</Link>.
            </p>
          </div>
        </div>
      )}

      {domains.isLoading ? (
        <div className="card">
          <TableSkeleton />
        </div>
      ) : domains.data?.length === 0 ? (
        <div className="card">
          <div className="empty">No domains yet.</div>
        </div>
      ) : (
        domains.data?.map((domain) => (
          <DomainCard
            key={domain.id}
            domain={domain}
            brand={brandFor(domain.brandId)}
            server={serverFor(domain.postalServerId)}
            domainSenders={sendersFor(domain.id)}
            instanceUrl={instanceFor(serverFor(domain.postalServerId)?.instanceId)?.baseUrl}
            onEditTracking={() => {
              const next = prompt(
                "Tracking domain — keeps rewritten links on this brand rather than pointing at the Postal host. Leave empty to unset.",
                domain.trackingDomain ?? "",
              );
              if (next !== null) {
                patchDomain.mutate({ id: domain.id, body: { trackingDomain: next || null } });
              }
            }}
            onRemove={async () => {
              if (
                await confirmAction({
                  title: `Delete ${domain.name}?`,
                  message:
                    "Its mail server and any unused senders go with it. Refused if a sender has already sent mail, since that is the record of what went out.",
                  confirmLabel: "Delete domain",
                  destructive: true,
                })
              ) {
                removeDomain.mutate(domain.id);
              }
            }}
            onRemoveSender={async (sender) => {
              if (
                await confirmAction({
                  title: `Remove ${sender.email}?`,
                  message:
                    "If it has already sent, it is deactivated instead so the record of what went out survives.",
                  confirmLabel: "Remove sender",
                  destructive: true,
                })
              ) {
                removeSender.mutate(sender.id);
              }
            }}
          />
        ))
      )}

      <button type="button" className="btn" onClick={() => setAdding(true)}>
        + Add domain
      </button>

      {adding && (
        <Drawer title="Add a sending domain" onClose={() => setAdding(false)}>
          <AddDomain
            brands={brands.data ?? []}
            instances={instances.data ?? []}
            unboundServers={(servers.data ?? []).filter(
              (s) => !domains.data?.some((d) => d.postalServerId === s.id),
            )}
            onDone={() => {
              setAdding(false);
              void qc.invalidateQueries({ queryKey: ["domains"] });
              void qc.invalidateQueries({ queryKey: ["postal-servers"] });
            }}
          />
        </Drawer>
      )}
    </div>
  );
}

/** One sending domain: its DNS state, mail server, credential and senders. */
function DomainCard({
  domain,
  brand,
  server,
  domainSenders,
  instanceUrl,
  onEditTracking,
  onRemove,
  onRemoveSender,
}: {
  domain: Domain;
  brand: Brand | undefined;
  server: PostalServer | undefined;
  domainSenders: Sender[];
  instanceUrl: string | undefined;
  onEditTracking: () => void;
  onRemove: () => void;
  onRemoveSender: (sender: Sender) => void;
}) {
  return (
    <div className="card" key={domain.id}>
      <div className="card-head">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="badge" style={{ background: brand?.color ?? "var(--dark)" }}>
            {brand?.badge ?? "??"}
          </span>
          <div>
            <h3 className="mono">{domain.name}</h3>
            <p>
              {brand?.name ?? "unknown brand"} · {domain.stream}
              {domain.isBurnable && " · burnable"}
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <DnsBadge label="SPF" status={domain.spfStatus} />
          <DnsBadge label="DKIM" status={domain.dkimStatus} />
          <DnsBadge label="MX" status={domain.mxStatus} />
          <DnsBadge label="Return-Path" status={domain.returnPathStatus} />
          <button type="button" className="btn ghost small" onClick={onEditTracking}>
            Edit
          </button>
          <button type="button" className="btn ghost small" onClick={onRemove}>
            Delete
          </button>
        </div>
      </div>

      <div className="card-body" style={{ paddingBottom: 8 }}>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <Fact label="Mail server" value={server?.name ?? "—"} />
          <Fact label="API key" value={server?.apiKeyHint ?? "not set"} mono />
          <Fact label="Tracking domain" value={domain.trackingDomain ?? "not set"} mono />
          <Fact
            label="Tracking"
            value={
              [server?.trackOpens && "opens", server?.trackClicks && "clicks"]
                .filter(Boolean)
                .join(" + ") || "off"
            }
          />
          {server && <HourlyLimit server={server} />}
        </div>

        {server && <CredentialCheck server={server} instanceUrl={instanceUrl} />}
        {domain.dnsError && (
          <div className="error" style={{ marginTop: 12, marginBottom: 0 }}>
            DNS: {domain.dnsError}
          </div>
        )}
      </div>

      <table>
        <thead>
          <tr>
            <th>Sender</th>
            <th>Today</th>
            <th>Warmup</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {domainSenders.length === 0 ? (
            <tr>
              <td colSpan={4} style={{ color: "var(--gray-500)" }}>
                No senders on this domain yet.
              </td>
            </tr>
          ) : (
            domainSenders.map((sender) => (
              <SenderRow key={sender.id} sender={sender} onRemove={() => onRemoveSender(sender)} />
            ))
          )}
        </tbody>
      </table>

      <AddSender domainId={domain.id} domainName={domain.name} stream={domain.stream} />
    </div>
  );
}

function warmupLabel(sender: Sender): { text: string; tone: string; note?: string } {
  if (!sender.warmupStartedAt) return { text: "Off", tone: "mute" };
  if (sender.warmupDay === null) return { text: "Complete", tone: "ok" };
  return {
    text: `Day ${sender.warmupDay} of ${sender.warmupDays}`,
    tone: "info",
    // The ramp advances on days this sender sent on, so an idle sender stays
    // where it is rather than arriving at full volume having sent nothing.
    note:
      sender.warmupDaysCompleted === 0
        ? "no sending days yet"
        : `${sender.warmupDaysCompleted} sending ${sender.warmupDaysCompleted === 1 ? "day" : "days"} so far`,
  };
}

/**
 * One sender: what it may send today — the ramped figure the scheduler will
 * actually enforce, not the ceiling — and an editor for its pacing.
 */
function SenderRow({ sender, onRemove }: { sender: Sender; onRemove: () => void }) {
  const [editing, setEditing] = useState(false);
  const warmup = warmupLabel(sender);
  const ramping = sender.todayCap < sender.dailyLimit;

  return (
    <>
      <tr>
        <td>
          <div style={{ fontWeight: 600 }}>{sender.displayName}</div>
          <div className="mono" style={{ color: "var(--gray-500)", fontSize: 11 }}>
            {sender.email}
          </div>
        </td>
        <td>
          <span className="mono" style={{ fontWeight: 600 }}>
            {sender.todayCap.toLocaleString()}/day
          </span>
          {ramping && <div className="hint">ramping to {sender.dailyLimit.toLocaleString()}</div>}
        </td>
        <td>
          <span className={`pill ${warmup.tone}`}>{warmup.text}</span>
          {warmup.note && <div className="hint">{warmup.note}</div>}
        </td>
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {!sender.isActive && <span className="pill bad">Inactive</span>}{" "}
          <button type="button" className="btn ghost small" onClick={() => setEditing((v) => !v)}>
            {editing ? "Close" : "Limits"}
          </button>{" "}
          <button type="button" className="btn ghost small" onClick={onRemove}>
            Remove
          </button>
        </td>
      </tr>
      {editing && (
        <tr>
          <td colSpan={4} style={{ background: "var(--gray-50)" }}>
            <SenderPacing sender={sender} onSaved={() => setEditing(false)} />
          </td>
        </tr>
      )}
    </>
  );
}

/** The ramp as a handful of points, so a change can be judged before saving. */
function rampPreview(start: number, limit: number, days: number): string {
  if (days <= 0 || start >= limit) return `${limit.toLocaleString()}/day throughout`;
  const at = (day: number) => Math.round(start + (limit - start) * (day / days));
  const marks = [0, Math.round(days / 3), Math.round((2 * days) / 3), days];
  return marks.map((d) => `day ${d + 1}: ${at(d).toLocaleString()}`).join(" → ");
}

function SenderPacing({ sender, onSaved }: { sender: Sender; onSaved: () => void }) {
  const qc = useQueryClient();
  const [dailyLimit, setDailyLimit] = useState(sender.dailyLimit);
  const [warmupStartDaily, setWarmupStartDaily] = useState(sender.warmupStartDaily);
  const [warmupDays, setWarmupDays] = useState(sender.warmupDays);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (extra: Record<string, unknown> = {}) =>
      api.patch(`/config/senders/${sender.id}`, {
        dailyLimit,
        warmupStartDaily,
        warmupDays,
        ...extra,
      }),
    onSuccess: () => {
      toast("Saved");
      setError(null);
      void qc.invalidateQueries({ queryKey: ["senders"] });
      onSaved();
    },
    onError: (err: Error) => setError(err.message),
  });

  const warming = Boolean(sender.warmupStartedAt) && sender.warmupDay !== null;

  return (
    <form
      className="pacing"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({});
      }}
    >
      {error && <div className="error">{error}</div>}

      <div className="row">
        <div className="field">
          <label htmlFor={`dl-${sender.id}`}>Daily limit</label>
          <input
            id={`dl-${sender.id}`}
            type="number"
            min={1}
            value={dailyLimit}
            onChange={(e) => setDailyLimit(Number(e.target.value))}
          />
          <div className="hint">
            The most this mailbox sends in a day once fully warmed up. Shared by every campaign and
            sequence using it.
          </div>
        </div>
        <div className="field">
          <label htmlFor={`ws-${sender.id}`}>Warmup starts at</label>
          <input
            id={`ws-${sender.id}`}
            type="number"
            min={1}
            value={warmupStartDaily}
            onChange={(e) => setWarmupStartDaily(Number(e.target.value))}
          />
          <div className="hint">Volume on the first day this sender sends.</div>
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor={`wd-${sender.id}`}>Warmup length (days)</label>
          <input
            id={`wd-${sender.id}`}
            type="number"
            min={1}
            max={365}
            value={warmupDays}
            onChange={(e) => setWarmupDays(Number(e.target.value))}
          />
        </div>
        <div className="field">
          {/* biome-ignore lint/a11y/noLabelWithoutControl: labels the preview text below. */}
          <label>Ramp</label>
          <div className="mono ramp">{rampPreview(warmupStartDaily, dailyLimit, warmupDays)}</div>
          <div className="hint">
            Days of sending, not days on the calendar. A day counts once this sender has used at
            least half that day&rsquo;s allowance, so an idle or barely used sender stays where it
            is instead of reaching full volume having sent nothing.
          </div>
        </div>
      </div>

      <div className="toolbar" style={{ marginBottom: 0 }}>
        <button className="btn small" type="submit" disabled={save.isPending}>
          Save limits
        </button>
        {warming ? (
          <button
            type="button"
            className="btn ghost small"
            onClick={async () => {
              if (
                await confirmAction({
                  title: "Stop the warmup?",
                  message: "This sender will go straight to its full daily limit.",
                  confirmLabel: "Stop warmup",
                  destructive: true,
                })
              )
                save.mutate({ warmupStartedAt: null });
            }}
          >
            Stop warmup
          </button>
        ) : null}
        <button
          type="button"
          className="btn ghost small"
          onClick={async () => {
            if (
              await confirmAction({
                title: warming ? "Restart the warmup from day 1?" : "Start a warmup from day 1?",
                message: warming
                  ? "Volume drops back to the starting figure."
                  : "Volume drops to the starting figure and climbs with each day this sender actually sends on.",
                confirmLabel: warming ? "Restart warmup" : "Start warmup",
              })
            )
              save.mutate({ warmupStartedAt: new Date().toISOString() });
          }}
        >
          {warming ? "Restart warmup" : "Start warmup"}
        </button>
      </div>
    </form>
  );
}

/**
 * A mail server's hourly cap — the throttle on its IP. Without one, a large
 * daily limit lets a broadcast go out in a single burst.
 */
function HourlyLimit({ server }: { server: PostalServer }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(server.hourlyLimit?.toString() ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/config/postal-servers/${server.id}`, {
        hourlyLimit: value.trim() === "" ? null : Number(value),
      }),
    onSuccess: () => {
      toast("Saved");
      setEditing(false);
      setError(null);
      void qc.invalidateQueries({ queryKey: ["postal-servers"] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (!editing) {
    return (
      <div>
        <div className="label-mono">Max per hour</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className="mono" style={{ fontWeight: 600 }}>
            {server.hourlyLimit === null ? "no limit" : server.hourlyLimit.toLocaleString()}
          </span>
          <button type="button" className="btn ghost small" onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="label-mono">Max per hour</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type="number"
          min={1}
          className="mono"
          style={{ width: 100 }}
          placeholder="no limit"
          aria-label="Maximum messages per hour"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button className="btn small" type="submit" disabled={save.isPending}>
          Save
        </button>
        <button type="button" className="btn ghost small" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
      <div className="hint" style={{ maxWidth: 280 }}>
        Keep it at or below Postal&rsquo;s own limit for this server. Empty for none.
      </div>
      {error && <div className="error">{error}</div>}
    </form>
  );
}

/** "3 minutes ago", so re-testing visibly changes something. */
function ago(iso: string): string {
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Tests a mail server's key against Postal and says what happened.
 *
 * Its own mutation per card: a single shared one disabled every Test button on
 * the page while any one of them was running, and gave no result at all when
 * an already-verified key verified again — which read as a button that did
 * nothing.
 */
function CredentialCheck({
  server,
  instanceUrl,
}: {
  server: PostalServer;
  instanceUrl: string | undefined;
}) {
  const qc = useQueryClient();
  const check = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; reason?: string }>(`/config/postal-servers/${server.id}/verify`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["postal-servers"] }),
  });

  const result = check.data;

  return (
    <div className="credential">
      <div className="credential-row">
        <div className="label-mono">Credential</div>
        {server.lastVerifiedAt ? (
          <span className="pill ok">Verified · {ago(server.lastVerifiedAt)}</span>
        ) : (
          <span className="pill bad">Not verified</span>
        )}
        <button
          type="button"
          className="btn ghost small"
          disabled={check.isPending}
          onClick={() => check.mutate()}
        >
          {check.isPending ? "Testing…" : "Test"}
        </button>
        <RotateKey serverId={server.id} />
      </div>

      {instanceUrl && (
        <div className="hint">
          Checked against <span className="mono">{instanceUrl}</span>
        </div>
      )}

      {/* The outcome of the click, stated — even when nothing changed. */}
      {check.isError && <div className="error">Could not reach the API: {check.error.message}</div>}
      {result?.ok && <div className="notice ok">Postal accepted this key just now.</div>}
      {result && !result.ok && (
        <div className="error">Postal refused this key: {result.reason}</div>
      )}
      {!result && server.lastVerifyError && <div className="error">{server.lastVerifyError}</div>}
    </div>
  );
}

/**
 * Replaces a mail server's API key. An inline password field rather than a
 * browser prompt, which would print the key on screen in plain text.
 */
function RotateKey({ serverId }: { serverId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);

  const rotate = useMutation({
    mutationFn: () =>
      api.patch<{ verified?: boolean }>(`/config/postal-servers/${serverId}`, { apiKey: key }),
    onSuccess: (row) => {
      if (row.verified === false) {
        setError("Saved, but Postal rejected this key too.");
      } else {
        setOpen(false);
        setError(null);
      }
      setKey("");
      void qc.invalidateQueries({ queryKey: ["postal-servers"] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (!open) {
    return (
      <button
        type="button"
        className="btn ghost small"
        style={{ marginLeft: 4 }}
        onClick={() => setOpen(true)}
      >
        Rotate key
      </button>
    );
  }

  return (
    <form
      className="rotate"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        rotate.mutate();
      }}
    >
      <input
        type="password"
        autoComplete="off"
        className="mono"
        placeholder="New API key"
        aria-label="New API key"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        required
      />
      <button className="btn small" type="submit" disabled={rotate.isPending}>
        Save
      </button>
      <button type="button" className="btn ghost small" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {error && <span className="rotate-error">{error}</span>}
    </form>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="label-mono">{label}</div>
      <div className={mono ? "mono" : undefined} style={{ fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}

function AddSender({
  domainId,
  domainName,
  stream,
}: {
  domainId: string;
  domainName: string;
  stream: Stream;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [warmUp, setWarmUp] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Served by the API, so the form shows exactly what will be applied.
  const defaults = useQuery({
    queryKey: ["sender-defaults"],
    queryFn: () => api.get<Record<Stream, SenderDefaults>>("/config/sender-defaults"),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const d = defaults.data?.[stream];
  const warming = warmUp ?? d?.warmUpByDefault ?? false;

  const create = useMutation({
    mutationFn: () =>
      api.post<Sender>("/config/senders", {
        domainId,
        email: `${local}@${domainName}`,
        displayName,
        // Only sent when the default was overridden; the API applies it otherwise.
        ...(warmUp === null ? {} : { warmupStartedAt: warmUp ? new Date().toISOString() : null }),
      }),
    onSuccess: () => {
      toast("Sender added");
      setOpen(false);
      setLocal("");
      setDisplayName("");
      setWarmUp(null);
      void qc.invalidateQueries({ queryKey: ["senders"] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (!open) {
    return (
      <div className="card-body" style={{ paddingTop: 10 }}>
        <button type="button" className="btn ghost small" onClick={() => setOpen(true)}>
          + Add sender
        </button>
      </div>
    );
  }

  return (
    <form
      className="card-body"
      style={{ background: "var(--gray-50)" }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
    >
      {error && <div className="error">{error}</div>}
      <div className="row">
        <div className="field">
          <label htmlFor={`local-${domainId}`}>Address</label>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              id={`local-${domainId}`}
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder="hello"
              required
            />
            {/* The API rejects an address off its own domain, because a
                mismatched From fails DKIM alignment. */}
            <span className="mono" style={{ color: "var(--gray-500)" }}>
              @{domainName}
            </span>
          </div>
        </div>
        <div className="field">
          <label htmlFor={`display-${domainId}`}>Display name</label>
          <input
            id={`display-${domainId}`}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </div>
      </div>

      {d && (
        <div className="field">
          <label className="checkline" htmlFor={`warm-${domainId}`}>
            <input
              id={`warm-${domainId}`}
              type="checkbox"
              checked={warming}
              onChange={(e) => setWarmUp(e.target.checked)}
            />
            Warm up — start at {d.warmupStartDaily.toLocaleString()}/day, reaching{" "}
            {d.dailyLimit.toLocaleString()}/day over {d.warmupDays} days
          </label>
          <div className="hint">
            {stream === "cold"
              ? "On by default for cold outreach: a new mailbox going straight to full volume is the likeliest way to get a domain blocklisted."
              : `Off by default for ${stream} mail. Turn it on if ${domainName} is new and has not sent before.`}{" "}
            Limits can be changed after adding.
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn small" type="submit" disabled={create.isPending}>
          Add sender
        </button>
        <button type="button" className="btn ghost small" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function AddDomain({
  brands,
  instances,
  unboundServers,
  onDone,
}: {
  brands: Brand[];
  instances: PostalInstance[];
  unboundServers: PostalServer[];
  onDone: () => void;
}) {
  const [brandId, setBrandId] = useState(brands[0]?.id ?? "");
  const [instanceId, setInstanceId] = useState(instances[0]?.id ?? "");
  const [apiKey, setApiKey] = useState("");
  /**
   * Binding an existing server is the rare path — it only exists for someone who
   * registered one separately — so the form defaults to pasting a key.
   */
  const [useExisting, setUseExisting] = useState(false);
  const [postalServerId, setPostalServerId] = useState(unboundServers[0]?.id ?? "");
  const [name, setName] = useState("");
  const [stream, setStream] = useState<Stream>("marketing");
  const [isBurnable, setIsBurnable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<Domain & { verified?: boolean }>("/config/domains", {
        brandId,
        name,
        stream,
        isBurnable,
        ...(useExisting ? { postalServerId } : { postalServer: { instanceId, apiKey } }),
      }),
    onSuccess: (domain) => {
      toast("Domain added");
      if (domain.verified === false) {
        // Saved regardless, so the key can be corrected in place rather than
        // re-entering the whole domain.
        setWarning(
          "Domain added, but Postal rejected that API key. Fix it with Rotate key on the domain.",
        );
      }
      onDone();
    },
    onError: (err: Error) => setError(err.message),
  });

  const missing =
    brands.length === 0
      ? "Create a brand first, under Brands."
      : instances.length === 0
        ? "Register your Postal installation first, under Postal."
        : null;

  return (
    <form
      className="card"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        setWarning(null);
        create.mutate();
      }}
    >
      <div className="card-body">
        {error && <div className="error">{error}</div>}
        {warning && <div className="notice warn">{warning}</div>}
        {missing && <div className="notice">{missing}</div>}

        <div className="row">
          <div className="field">
            <label htmlFor="d-name">Domain</label>
            <input
              id="d-name"
              className="mono"
              value={name}
              onChange={(e) => setName(e.target.value.trim().toLowerCase())}
              placeholder="mail.example.com"
              required
            />
          </div>
          <div className="field">
            <label htmlFor="d-brand">Brand</label>
            <select
              id="d-brand"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
              required
            >
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label htmlFor="d-stream">Stream</label>
          <select
            id="d-stream"
            value={stream}
            onChange={(e) => {
              const next = e.target.value as Stream;
              setStream(next);
              if (next !== "cold") setIsBurnable(false);
            }}
          >
            <option value="transactional">Transactional</option>
            <option value="marketing">Marketing</option>
            <option value="cold">Cold outreach</option>
          </select>
        </div>

        {stream === "cold" && (
          <div className="field">
            <label htmlFor="d-burnable">Burnable</label>
            <label className="checkline">
              <input
                id="d-burnable"
                type="checkbox"
                checked={isBurnable}
                onChange={(e) => setIsBurnable(e.target.checked)}
              />
              A lookalike domain we are willing to lose
            </label>
            <div className="hint">
              Cold outreach should run on domains that can be discarded, not on a brand&rsquo;s real
              one.
            </div>
          </div>
        )}

        <div className="subsection">
          <div className="label-mono">Postal mail server</div>

          {useExisting ? (
            <div className="field">
              <select
                id="d-server"
                aria-label="Existing mail server"
                value={postalServerId}
                onChange={(e) => setPostalServerId(e.target.value)}
                required
              >
                {unboundServers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="row">
              <div className="field">
                <label htmlFor="d-instance">Installation</label>
                <select
                  id="d-instance"
                  value={instanceId}
                  onChange={(e) => setInstanceId(e.target.value)}
                  required
                >
                  {instances.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="d-key">API key</label>
                <input
                  id="d-key"
                  type="password"
                  autoComplete="off"
                  className="mono"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  required
                />
                <div className="hint">
                  From the mail server you created in Postal for this domain. It is checked against
                  Postal and stored encrypted.
                </div>
              </div>
            </div>
          )}

          {unboundServers.length > 0 && (
            <button type="button" className="linkish" onClick={() => setUseExisting((v) => !v)}>
              {useExisting
                ? "Paste an API key instead"
                : `Or use one of ${unboundServers.length} already-registered server${
                    unboundServers.length === 1 ? "" : "s"
                  }`}
            </button>
          )}
        </div>

        <button className="btn" type="submit" disabled={create.isPending || Boolean(missing)}>
          {create.isPending ? "Checking key…" : "Add domain"}
        </button>
      </div>
    </form>
  );
}
